import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useSocket } from "@/contexts/SocketContext";
import { useAuth } from "@/contexts/AuthContext";
import { authPost, authGet } from "@/utils/api";
import JanusClient from "@/services/JanusClient";

/**
 * ActiveCallContext
 * Gerencia estado global de chamada ativa.
 * Persiste entre navegacoes de ticket — a chamada so encerra com acao explicita.
 */
const ActiveCallContext = createContext(null);

/**
 * @typedef {Object} ActiveCall
 * @property {string} dbCallId - DB call ID
 * @property {string} callId - Meta call ID
 * @property {string} contactName - Nome do contato
 * @property {string} contactPhone - Telefone do contato
 * @property {string|null} contactAvatar - URL do avatar
 * @property {string} contactId - Contact ID
 * @property {string|null} ticketId - Ticket ID associado
 * @property {string} status - RINGING | ACCEPTED | IN_PROGRESS | COMPLETED | FAILED | TERMINATED
 * @property {number} startedAt - timestamp ms
 * @property {boolean} muted - mic muted
 * @property {RTCIceServer[]|null} [iceServers] - ICE servers from the API,
 *   including the TURN relay when enabled. Persisted with the call so a restore
 *   after page reload keeps the relay instead of dropping back to STUN-only.
 */

const STORAGE_KEY = "tw:activeCall";

/** Try to restore persisted call state from localStorage */
function loadPersistedCall() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Sanity: discard if older than 2h (calls shouldn't last that long)
    if (data.startedAt && Date.now() - data.startedAt > 2 * 60 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    // Discard terminal states that somehow got persisted
    if (["COMPLETED", "FAILED", "TERMINATED", "MISSED"].includes(data.status)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function persistCall(call) {
  try {
    if (call) {
      // Don't persist transcripts array (too large, transient)
      const { transcripts, ...rest } = call;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // quota exceeded or similar — ignore
  }
}

export function ActiveCallProvider({ children }) {
  const { socket } = useSocket();
  const { user } = useAuth();
  const userIdRef = useRef(user?.id); // Ref for stable access in socket handlers
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  const [activeCall, setActiveCall] = useState(() => loadPersistedCall());
  const [pendingCalls, setPendingCalls] = useState([]); // Calls received while busy
  const timerRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const validatedRef = useRef(false);
  const janusRef = useRef(null); // JanusClient instance
  const audioRef = useRef(null); // Hidden <audio> element for remote playback
  const acceptingRef = useRef(false); // Guard against multiple accept clicks
  const switchingRef = useRef(false); // Guard against multiple switch-call clicks
  const endCallRef = useRef(null); // Ref to endCall for use in connectToJanus callbacks

  // Persist call state to localStorage on every change
  useEffect(() => {
    persistCall(activeCall);
  }, [activeCall]);

  // On mount: validate persisted call is still active on the server
  useEffect(() => {
    if (!activeCall?.dbCallId || validatedRef.current) return;
    validatedRef.current = true;

    authGet(`/api/calls/${activeCall.dbCallId}`)
      .then((res) => res.json())
      .then((json) => {
        const call = json?.data;
        if (
          !call ||
          ["COMPLETED", "FAILED", "TERMINATED", "MISSED"].includes(call.status)
        ) {
          console.log(
            "[ActiveCall] Persisted call no longer active — clearing",
          );
          setActiveCall(null);
        } else {
          if (call.status !== activeCall.status) {
            setActiveCall((prev) =>
              prev ? { ...prev, status: call.status } : null,
            );
          }
          // Restore Janus connection if call is active and we have connection info
          if (
            ["ACCEPTED", "IN_PROGRESS"].includes(call.status) &&
            activeCall.janusRoomId &&
            activeCall.janusWsUrl &&
            !janusRef.current
          ) {
            console.log(
              "[ActiveCall] Restoring Janus connection after page reload",
            );
            connectToJanus(
              activeCall.janusWsUrl,
              activeCall.janusRoomId,
              activeCall.janusToken,
              activeCall.iceServers,
            );
          }
        }
      })
      .catch((err) => {
        console.warn(
          "[ActiveCall] Could not validate persisted call:",
          err.message,
        );
        // Don't clear — may be a network blip; let socket events sort it out
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Timer: conta segundos desde o inicio da chamada
  useEffect(() => {
    if (
      activeCall &&
      ["ACCEPTED", "IN_PROGRESS"].includes(activeCall.status) &&
      activeCall.startedAt
    ) {
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - activeCall.startedAt) / 1000));
      }, 1000);
    } else {
      setElapsed(0);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeCall?.status, activeCall?.startedAt]);

  // Helper: play remote audio stream through a hidden <audio> element
  const playRemoteAudio = useCallback((remoteStream) => {
    if (!audioRef.current) {
      audioRef.current = document.createElement("audio");
      audioRef.current.autoplay = true;
      audioRef.current.style.display = "none";
      document.body.appendChild(audioRef.current);
    }
    audioRef.current.srcObject = remoteStream;
    audioRef.current.play().catch(() => {
      console.warn("[ActiveCall] Audio autoplay blocked — user gesture needed");
    });
  }, []);

  // Helper: connect browser to Janus AudioBridge for bidirectional audio
  const connectToJanus = useCallback(
    async (janusWsUrl, janusRoomId, janusToken, iceServers) => {
      try {
        console.log(
          "[ActiveCall] Connecting to Janus AudioBridge:",
          janusWsUrl,
          "Room:",
          janusRoomId,
          iceServers?.length ? `(${iceServers.length} ICE servers)` : "(STUN)",
        );

        const client = new JanusClient(janusWsUrl, {
          apiSecret: janusToken,
          iceServers,
          onReconnecting: (attempt) => {
            console.log("[ActiveCall] Janus reconnecting... attempt", attempt);
            setActiveCall((prev) =>
              prev ? { ...prev, audioState: "reconnecting" } : null,
            );
          },
          onReconnected: (newStream) => {
            console.log("[ActiveCall] Janus reconnected — audio restored");
            setActiveCall((prev) =>
              prev ? { ...prev, audioState: "connected" } : null,
            );
            if (newStream) playRemoteAudio(newStream);
          },
          onReconnectFailed: () => {
            console.error(
              "[ActiveCall] Janus reconnection failed — auto-ending call",
            );
            setActiveCall((prev) =>
              prev ? { ...prev, audioState: "failed" } : null,
            );
            // Auto-terminate: no point keeping a call with no audio
            if (endCallRef.current) {
              endCallRef.current();
            }
          },
        });
        await client.connect();
        const remoteStream = await client.joinRoom(janusRoomId);
        janusRef.current = client;

        playRemoteAudio(remoteStream);

        // Persist Janus connection info for restore after page reload.
        // iceServers rides along so a restore keeps the TURN relay instead of
        // silently falling back to STUN — which on a restrictive network means
        // the audio never comes back after an F5.
        setActiveCall((prev) =>
          prev
            ? {
                ...prev,
                audioState: "connected",
                janusRoomId,
                janusWsUrl,
                janusToken: janusToken || null,
                iceServers: iceServers || null,
              }
            : null,
        );

        console.log("[ActiveCall] Janus AudioBridge connected — audio active");
      } catch (err) {
        console.error("[ActiveCall] Failed to connect to Janus:", err);
        setActiveCall((prev) =>
          prev ? { ...prev, audioState: "failed" } : null,
        );
      }
    },
    [playRemoteAudio],
  );

  // Helper: disconnect Janus and clean up audio
  const disconnectJanus = useCallback(() => {
    if (janusRef.current) {
      janusRef.current.disconnect();
      janusRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.srcObject = null;
      audioRef.current.remove();
      audioRef.current = null;
    }
  }, []);

  // Iniciar chamada — seta estado global e conecta ao Janus para outbound
  const startCall = useCallback(
    async (callData) => {
      setActiveCall({
        dbCallId: callData.dbCallId,
        callId: callData.callId,
        contactName: callData.contactName,
        contactPhone: callData.contactPhone,
        contactAvatar: callData.contactAvatar || null,
        contactId: callData.contactId,
        ticketId: callData.ticketId || null,
        direction: "BUSINESS_INITIATED",
        status: "RINGING",
        startedAt: null,
        muted: false,
      });

      // For outbound calls, connect browser to Janus AudioBridge immediately
      if (callData.janusRoomId && callData.janusWsUrl) {
        await connectToJanus(
          callData.janusWsUrl,
          callData.janusRoomId,
          callData.janusToken,
          callData.iceServers,
        );
      }
    },
    [connectToJanus],
  );

  // Encerrar chamada via API
  const endCall = useCallback(async () => {
    if (!activeCall) return;

    disconnectJanus();

    try {
      await authPost(`/api/calls/${activeCall.dbCallId}/end`);
    } catch (err) {
      console.error("[ActiveCall] Failed to end call:", err);
    }

    setActiveCall(null);
  }, [activeCall, disconnectJanus]);

  // Keep endCallRef in sync so connectToJanus callbacks can call it
  useEffect(() => {
    endCallRef.current = endCall;
  }, [endCall]);

  // Toggle mute — mutes local mic track + tells Janus AudioBridge
  const toggleMute = useCallback(async () => {
    if (!activeCall) return;

    const newMuted = !activeCall.muted;

    // Mute local mic via JanusClient (disables track + AudioBridge mute)
    if (janusRef.current) {
      janusRef.current.setMuted(newMuted);
    }

    // Optimistic UI update
    setActiveCall((prev) => (prev ? { ...prev, muted: newMuted } : null));

    // Also notify backend (for AI calls / recording)
    try {
      await authPost(`/api/calls/${activeCall.dbCallId}/mute`, {
        muted: newMuted,
      });
    } catch (err) {
      console.warn(
        "[ActiveCall] Mute API call failed (local mute still active):",
        err.message,
      );
    }
  }, [activeCall]);

  // Handoff AI -> humano
  const handoff = useCallback(async () => {
    if (!activeCall) return;

    try {
      await authPost(`/api/calls/${activeCall.dbCallId}/handoff`);
    } catch (err) {
      console.error("[ActiveCall] Failed to handoff:", err);
    }
  }, [activeCall]);

  // Escutar eventos de socket para atualizar status
  useEffect(() => {
    if (!socket) return;

    // Chamada recebida (incoming) — setar como chamada ativa ou adicionar como pendente
    const handleIncomingCall = (data) => {
      const callPayload = {
        dbCallId: data.dbCallId,
        callId: data.callId,
        contactName: data.contactName || data.contactPhone,
        contactPhone: data.contactPhone,
        contactAvatar: null,
        contactId: data.contactId,
        ticketId: data.ticketId || null,
        status: "RINGING",
        startedAt: null,
        muted: false,
        direction: data.direction || "USER_INITIATED",
        handlerType: data.handlerType || "HUMAN",
      };

      setActiveCall((prev) => {
        if (prev) {
          // Já tem chamada ativa → coloca na fila de pendentes
          console.log(
            "[ActiveCall] Already in call — queuing pending call:",
            data.callId,
          );
          setPendingCalls((pending) => {
            // Evita duplicatas
            if (pending.some((c) => c.callId === data.callId)) return pending;
            return [...pending, callPayload];
          });
          return prev;
        }

        console.log(
          "[ActiveCall] Incoming call:",
          data.callId,
          data.contactPhone,
        );
        return callPayload;
      });
    };

    const handleStatusUpdate = (data) => {
      const newStatus = data.status;
      const isTerminal = [
        "COMPLETED",
        "FAILED",
        "TERMINATED",
        "MISSED",
      ].includes(newStatus);

      // Limpar da fila de pendentes se for estado terminal
      if (isTerminal) {
        setPendingCalls((pending) =>
          pending.filter(
            (c) => c.dbCallId !== data.dbCallId && c.callId !== data.callId,
          ),
        );
      }

      setActiveCall((prev) => {
        if (!prev) return null;
        if (data.dbCallId !== prev.dbCallId && data.callId !== prev.callId)
          return prev;

        // Terminal states — limpar chamada ativa e desconectar Janus
        if (isTerminal) {
          disconnectJanus();
          return null;
        }

        // Se a chamada INCOMING foi ACCEPTED mas este agente NÃO está aceitando,
        // significa que outro agente aceitou — dismiss para este agente.
        // Para chamadas OUTBOUND (que eu iniciei), ACCEPTED = contato atendeu → manter.
        if (
          newStatus === "ACCEPTED" &&
          prev.status === "RINGING" &&
          !acceptingRef.current &&
          prev.direction !== "BUSINESS_INITIATED"
        ) {
          console.log(
            "[ActiveCall] Call accepted by another agent (via call:status) — dismissing",
          );
          disconnectJanus();
          return null;
        }

        // Start timer when call is actually answered
        const startedAt =
          ["ACCEPTED", "IN_PROGRESS"].includes(newStatus) && !prev.startedAt
            ? Date.now()
            : prev.startedAt;
        return { ...prev, status: newStatus, startedAt };
      });
    };

    const handleCallEnd = (data) => {
      // Limpar da fila de pendentes
      setPendingCalls((pending) =>
        pending.filter(
          (c) => c.dbCallId !== data.dbCallId && c.callId !== data.callId,
        ),
      );

      setActiveCall((prev) => {
        if (!prev) return null;
        if (data.dbCallId !== prev.dbCallId && data.callId !== prev.callId)
          return prev;
        disconnectJanus();
        return null;
      });
    };

    // Transcript events — for real-time transcript display
    const handleTranscript = (data) => {
      setActiveCall((prev) => {
        if (!prev) return null;
        if (data.dbCallId !== prev.dbCallId && data.callId !== prev.callId)
          return prev;

        const transcripts = [
          ...(prev.transcripts || []),
          {
            speaker: data.speaker,
            text: data.text,
            timestamp: data.timestamp,
          },
        ];

        return { ...prev, transcripts };
      });
    };

    // AI speaking state — for UI indicator
    const handleAiSpeaking = (data) => {
      setActiveCall((prev) => {
        if (!prev) return null;
        if (data.callId !== prev.callId) return prev;
        return { ...prev, aiSpeaking: !!data.speaking };
      });
    };

    // Agent joined call
    const handleAgentJoined = (data) => {
      setActiveCall((prev) => {
        if (!prev) return null;
        if (data.dbCallId !== prev.dbCallId && data.callId !== prev.callId)
          return prev;
        // Se outro agente atendeu e eu ainda estou em RINGING, ignorar
        // (o call:answered_by vai limpar logo em seguida)
        if (data.agentId !== userIdRef.current && prev.status === "RINGING") {
          return prev;
        }
        return { ...prev, handlerType: "HYBRID", agentId: data.agentId };
      });
    };

    // Another agent answered the call — dismiss ringing for this agent
    const handleAnsweredBy = (data) => {
      // Limpar da fila de pendentes (outro agente atendeu)
      setPendingCalls((pending) =>
        pending.filter(
          (c) => c.dbCallId !== data.dbCallId && c.callId !== data.callId,
        ),
      );

      setActiveCall((prev) => {
        if (!prev) return null;
        if (data.dbCallId !== prev.dbCallId && data.callId !== prev.callId)
          return prev;
        // If the current user is NOT the agent who answered, clear the call
        const myId = userIdRef.current;
        if (data.agentId && myId && data.agentId !== myId) {
          console.log(
            "[ActiveCall] Call answered by another agent:",
            data.agentId,
            "| my id:",
            myId,
          );
          disconnectJanus();
          return null;
        }
        return prev;
      });
    };

    // Call was transferred away from this agent to another
    const handleTransferredAway = (data) => {
      setActiveCall((prev) => {
        if (!prev) return null;
        if (data.dbCallId !== prev.dbCallId) return prev;
        // Only disconnect if the current user is the one who transferred
        if (
          data.fromAgentId &&
          userIdRef.current &&
          data.fromAgentId === userIdRef.current
        ) {
          console.log(
            "[ActiveCall] Call transferred to:",
            data.toAgentName || data.toAgentId,
          );
          disconnectJanus();
          return null;
        }
        return prev;
      });
    };

    // Two-phase transfer: source agent gets "pending" confirmation
    const handleTransferPending = (data) => {
      setActiveCall((prev) => {
        if (!prev) return null;
        if (data.dbCallId !== prev.dbCallId) return prev;
        console.log("[ActiveCall] Transfer pending to:", data.toAgentName);
        return {
          ...prev,
          pendingTransfer: {
            toAgentId: data.toAgentId,
            toAgentName: data.toAgentName,
          },
        };
      });
    };

    // Two-phase transfer: target agent receives transfer proposal
    const handleTransferProposed = (data) => {
      // Only handle if targeted at this user
      if (data.toAgentId !== user?.id) return;

      setActiveCall((prev) => {
        if (prev) {
          console.warn(
            "[ActiveCall] Transfer proposed ignored — already in call:",
            prev.callId,
          );
          return prev;
        }

        console.log("[ActiveCall] Incoming transfer from:", data.fromAgentName);
        return {
          dbCallId: data.dbCallId,
          callId: data.callId,
          contactName: data.contactName || data.contactPhone,
          contactPhone: data.contactPhone,
          contactAvatar: null,
          contactId: data.contactId,
          ticketId: data.ticketId || null,
          status: "RINGING",
          startedAt: null,
          muted: false,
          direction: data.direction || "USER_INITIATED",
          isTransfer: true,
          transferFrom: {
            agentId: data.fromAgentId,
            agentName: data.fromAgentName,
          },
          janusRoomId: data.janusRoomId,
          janusWsUrl: data.janusWsUrl,
          janusToken: data.janusToken || null,
          iceServers: data.iceServers || null,
        };
      });
    };

    // Two-phase transfer: source agent learns transfer was accepted
    const handleTransferAccepted = (data) => {
      setActiveCall((prev) => {
        if (!prev) return null;
        if (data.dbCallId !== prev.dbCallId) return prev;
        if (data.fromAgentId === userIdRef.current) {
          console.log("[ActiveCall] Transfer accepted by:", data.toAgentName);
          disconnectJanus();
          return null;
        }
        return prev;
      });
    };

    // Two-phase transfer: source agent learns transfer was rejected
    const handleTransferRejected = (data) => {
      setActiveCall((prev) => {
        if (!prev) return null;
        if (data.dbCallId !== prev.dbCallId) return prev;
        console.log(
          "[ActiveCall] Transfer rejected by:",
          data.toAgentName,
          "| Reason:",
          data.reason,
        );
        // Clear pendingTransfer — call stays with this agent
        return {
          ...prev,
          pendingTransfer: null,
          transferRejection: {
            toAgentName: data.toAgentName,
            reason: data.reason,
          },
        };
      });
    };

    socket.on("call:incoming", handleIncomingCall);
    socket.on("call:status", handleStatusUpdate);
    socket.on("call:ended", handleCallEnd);
    socket.on("call:terminated", handleCallEnd);
    socket.on("call:transcript", handleTranscript);
    socket.on("call:ai_speaking", handleAiSpeaking);
    socket.on("call:agent_joined", handleAgentJoined);
    socket.on("call:answered_by", handleAnsweredBy);
    socket.on("call:transferred_away", handleTransferredAway);
    socket.on("call:transfer_pending", handleTransferPending);
    socket.on("call:transfer_proposed", handleTransferProposed);
    socket.on("call:transfer_accepted", handleTransferAccepted);
    socket.on("call:transfer_rejected", handleTransferRejected);

    return () => {
      socket.off("call:incoming", handleIncomingCall);
      socket.off("call:status", handleStatusUpdate);
      socket.off("call:ended", handleCallEnd);
      socket.off("call:terminated", handleCallEnd);
      socket.off("call:transcript", handleTranscript);
      socket.off("call:ai_speaking", handleAiSpeaking);
      socket.off("call:agent_joined", handleAgentJoined);
      socket.off("call:answered_by", handleAnsweredBy);
      socket.off("call:transferred_away", handleTransferredAway);
      socket.off("call:transfer_pending", handleTransferPending);
      socket.off("call:transfer_proposed", handleTransferProposed);
      socket.off("call:transfer_accepted", handleTransferAccepted);
      socket.off("call:transfer_rejected", handleTransferRejected);
    };
  }, [socket]);

  // Aceitar chamada manualmente (para chamadas que aparecem como RINGING)
  const acceptCall = useCallback(async () => {
    if (!activeCall) return;
    // Guard against multiple rapid clicks
    if (acceptingRef.current) {
      console.warn(
        "[ActiveCall] Accept already in progress — ignoring duplicate click",
      );
      return;
    }
    acceptingRef.current = true;

    try {
      const res = await authPost(`/api/calls/${activeCall.dbCallId}/accept`);
      const json = await res.json();

      if (!res.ok) {
        console.error(
          "[ActiveCall] Accept API returned error:",
          json?.message || res.status,
        );
        return;
      }

      const data = json?.data;

      // Optimistically update status so UI reflects accepted state
      setActiveCall((prev) =>
        prev ? { ...prev, status: "ACCEPTED", startedAt: Date.now() } : null,
      );

      // Connect to Janus AudioBridge if room info is returned
      if (data?.janusRoomId && data?.janusWsUrl) {
        await connectToJanus(
          data.janusWsUrl,
          data.janusRoomId,
          data.janusToken,
          data.iceServers,
        );
      } else {
        console.warn(
          "[ActiveCall] No Janus room info in accept response — audio may not work",
        );
      }
    } catch (err) {
      console.error("[ActiveCall] Failed to accept call:", err);
    } finally {
      acceptingRef.current = false;
    }
  }, [activeCall, connectToJanus]);

  // Rejeitar chamada
  const rejectCall = useCallback(async () => {
    if (!activeCall) return;

    disconnectJanus();

    try {
      await authPost(`/api/calls/${activeCall.dbCallId}/reject`);
    } catch (err) {
      console.error("[ActiveCall] Failed to reject call:", err);
    }

    setActiveCall(null);
  }, [activeCall, disconnectJanus]);

  // Accept an incoming transfer (two-phase)
  const acceptTransfer = useCallback(async () => {
    if (!activeCall || !activeCall.isTransfer) return;

    try {
      const res = await authPost(
        `/api/calls/${activeCall.dbCallId}/transfer/accept`,
      );
      const json = await res.json();
      const data = json?.data;

      // Connect to Janus AudioBridge
      const roomId = data?.janusRoomId || activeCall.janusRoomId;
      const wsUrl = data?.janusWsUrl || activeCall.janusWsUrl;
      const token = data?.janusToken || activeCall.janusToken;
      const ice = data?.iceServers || activeCall.iceServers;
      if (roomId && wsUrl) {
        await connectToJanus(wsUrl, roomId, token, ice);
      } else {
        console.warn("[ActiveCall] No Janus room info for accepted transfer");
      }

      // Update call state: no longer ringing, now active
      setActiveCall((prev) =>
        prev
          ? {
              ...prev,
              status: "IN_PROGRESS",
              isTransfer: false,
              transferFrom: null,
            }
          : null,
      );
    } catch (err) {
      console.error("[ActiveCall] Failed to accept transfer:", err);
    }
  }, [activeCall, connectToJanus]);

  // Reject an incoming transfer (two-phase)
  const rejectTransfer = useCallback(
    async (reason = "") => {
      if (!activeCall || !activeCall.isTransfer) return;

      try {
        await authPost(`/api/calls/${activeCall.dbCallId}/transfer/reject`, {
          reason,
        });
      } catch (err) {
        console.error("[ActiveCall] Failed to reject transfer:", err);
      }

      setActiveCall(null);
    },
    [activeCall],
  );

  // Clear transfer rejection notification
  const clearTransferRejection = useCallback(() => {
    setActiveCall((prev) =>
      prev ? { ...prev, transferRejection: null } : null,
    );
  }, []);

  // Remover chamada da fila de pendentes (ignorar)
  const dismissPendingCall = useCallback((callId) => {
    setPendingCalls((pending) => pending.filter((c) => c.callId !== callId));
  }, []);

  /**
   * switchToCall — Aceita uma chamada pendente (inbound).
   * Se a chamada atual está em andamento (ACCEPTED/IN_PROGRESS), encerra antes
   * ("Encerrar e atender"). Se está apenas tocando (2ª chamada simultânea),
   * devolve para a fila de pendentes em vez de encerrar ("Atender esta").
   * Extensível para V2: pode ser reutilizado para transfer-while-busy, hold, etc.
   */
  const switchToCall = useCallback(
    async (pendingCall) => {
      if (switchingRef.current) return;
      switchingRef.current = true;
      acceptingRef.current = true; // Guard: prevent handleStatusUpdate from dismissing

      try {
        // 1. Lidar com a chamada atual:
        //    - Em andamento (ACCEPTED/IN_PROGRESS) → encerrar ("Encerrar e atender").
        //    - Apenas tocando (RINGING — ex.: 2ª chamada simultânea) → NÃO encerrar;
        //      devolver para a fila de pendentes para continuar disponível ("Atender esta").
        const current = activeCall;
        const currentOngoing =
          !!current && ["ACCEPTED", "IN_PROGRESS"].includes(current.status);
        if (currentOngoing) {
          disconnectJanus();
          try {
            await authPost(`/api/calls/${current.dbCallId}/end`);
          } catch (err) {
            console.error(
              "[ActiveCall] Failed to end current call during switch:",
              err,
            );
          }
        }

        // 2. Atualizar fila: remove a escolhida; recoloca a anterior se só estava
        //    tocando (assim ela continua disponível em vez de sumir).
        setPendingCalls((pending) => {
          let next = pending.filter((c) => c.callId !== pendingCall.callId);
          if (
            current &&
            !currentOngoing &&
            current.callId !== pendingCall.callId &&
            !next.some((c) => c.callId === current.callId)
          ) {
            next = [...next, current];
          }
          return next;
        });

        // 3. Setar como chamada ativa
        setActiveCall(pendingCall);

        // 4. Aceitar a nova chamada
        try {
          const res = await authPost(
            `/api/calls/${pendingCall.dbCallId}/accept`,
          );
          const json = await res.json();

          if (!res.ok) {
            console.error(
              "[ActiveCall] Switch accept failed:",
              json?.message || res.status,
            );
            setActiveCall(null);
            return;
          }

          const data = json?.data;

          setActiveCall((prev) =>
            prev
              ? { ...prev, status: "ACCEPTED", startedAt: Date.now() }
              : null,
          );

          if (data?.janusRoomId && data?.janusWsUrl) {
            await connectToJanus(
              data.janusWsUrl,
              data.janusRoomId,
              data.janusToken,
              data.iceServers,
            );
          }
        } catch (err) {
          console.error("[ActiveCall] Failed to accept after switch:", err);
          setActiveCall(null);
        }
      } finally {
        switchingRef.current = false;
        acceptingRef.current = false;
      }
    },
    [activeCall, disconnectJanus, connectToJanus],
  );

  /**
   * endCurrentCall — Encerra a chamada atual, limpa Janus, e retorna.
   * Usado como building block para switchAndInitiateCall (chamado pelo ChatPanel).
   * Não limpa activeCall — o caller é responsável por setar a nova chamada.
   */
  const endCurrentCall = useCallback(async () => {
    if (!activeCall) return;
    disconnectJanus();
    try {
      await authPost(`/api/calls/${activeCall.dbCallId}/end`);
    } catch (err) {
      console.error("[ActiveCall] Failed to end current call:", err);
    }
    setActiveCall(null);
  }, [activeCall, disconnectJanus]);

  const value = {
    activeCall,
    elapsed,
    pendingCalls,
    startCall,
    endCall,
    endCurrentCall,
    acceptCall,
    rejectCall,
    acceptTransfer,
    rejectTransfer,
    clearTransferRejection,
    toggleMute,
    handoff,
    switchToCall,
    dismissPendingCall,
    hasActiveCall: !!activeCall,
    isSwitching: switchingRef.current,
  };

  return (
    <ActiveCallContext.Provider value={value}>
      {children}
    </ActiveCallContext.Provider>
  );
}

/**
 * Safe no-op defaults when used outside provider (e.g. Vite HMR invalidation).
 * Prevents crash without masking real usage errors — logs a warning once.
 */
const SAFE_DEFAULTS = {
  activeCall: null,
  elapsed: 0,
  pendingCalls: [],
  startCall: () => {},
  endCall: async () => {},
  endCurrentCall: async () => {},
  acceptCall: async () => {},
  rejectCall: async () => {},
  acceptTransfer: async () => {},
  rejectTransfer: async () => {},
  clearTransferRejection: () => {},
  toggleMute: () => {},
  handoff: async () => {},
  switchToCall: async () => {},
  dismissPendingCall: () => {},
  hasActiveCall: false,
  isSwitching: false,
};

let _warnedOutsideProvider = false;

export function useActiveCall() {
  const context = useContext(ActiveCallContext);
  if (!context) {
    if (!_warnedOutsideProvider) {
      console.warn(
        "[ActiveCall] useActiveCall used outside ActiveCallProvider — returning safe defaults (HMR?)",
      );
      _warnedOutsideProvider = true;
    }
    return SAFE_DEFAULTS;
  }
  _warnedOutsideProvider = false;
  return context;
}
